import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { TargetMenu } from 'models';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  displayError,
  handleServiceError,
  TARGET_MENU_BAR_GUIDANCE,
  TARGET_MENU_DAILY_MENU,
  TARGET_MENU_DISPLAY_CONTROL_CONDITION,
  TARGET_MENU_DISPLAY_CONTROL_TIMING,
  TARGET_MENU_ORDER_QUANTITY_LIMIT,
  TARGET_MENU_OVERLAY_FUNCTION,
  TARGET_MENU_SERVICE,
  TARGET_MENU_SHABU_SUKIYAKI,
} from 'utils';
import SelectTargetMenuModalFilter, { FormSearchValueProps } from './components/SelectTargetMenuModalFilter';
import SelectTargetMenuModalTable from './components/SelectTargetMenuModalTable';
import targetMenuService from './services';
import './style.scss';
import { SearchTargetMenuPayoad } from './types';

// mockdata
// const MOCKUP_TARGET_MENUS: TargetMenu[] = Array.from({ length: 200 }, (_, index) => ({
//   id: index + 1,
//   coupon_classification: Math.floor(Math.random() * 3),
//   cd: `CD-${String(index + 1).padStart(3, '0')}`,
//   name: `Menu Item ${index + 1}`,
// }));

// A-036
export interface SelectTargetMenuModalProps {
  targetSourceType?: number;
  targetName?: string;
  exceptMenuIds?: number[];
  onReflect: (menuIds: number[]) => void;
  onClose: () => void;
}

function SelectTargetMenuModal({
  targetSourceType = 0,
  targetName = '',
  exceptMenuIds = [],
  onClose,
  onReflect,
}: SelectTargetMenuModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [targetMenus, setTargetMenus] = useState<TargetMenu[]>([]);
  const [filter, setFilter] = useState<FormSearchValueProps>({
    menuClassificationId: '',
    name: '',
    cd: '',
  });

  // effects
  useEffect(() => {
    loadTargetMenuData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // functions
  function loadTargetMenuData() {
    (async () => {
      try {
        const payload: SearchTargetMenuPayoad = {};
        payload.target_menu_type = targetSourceType;
        payload.menu_classification_id =
          filter.menuClassificationId.trim().length > 0 ? Number(filter.menuClassificationId) : undefined;
        payload.name = filter.name.trim().length > 0 ? filter.name : undefined;
        payload.cd = filter.cd.trim().length > 0 ? filter.cd : undefined;
        payload.except_menu_ids = exceptMenuIds;
        const response = await targetMenuService.getMenus(payload);
        setTargetMenus(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  // events
  function handleClose() {
    onClose?.();
  }

  function getTargetMenuName() {
    let targetMenuName: string | null = null;

    if (targetSourceType === TARGET_MENU_BAR_GUIDANCE) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_bar_guidance', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_OVERLAY_FUNCTION) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_overlay_function', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_DAILY_MENU) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_daily_menu', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_ORDER_QUANTITY_LIMIT) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_quantity_order_limit', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_SHABU_SUKIYAKI) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_shabu_sukiyaki', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_DISPLAY_CONTROL_CONDITION) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_display_control_condition');
    } else if (targetSourceType === TARGET_MENU_DISPLAY_CONTROL_TIMING) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_display_control_timing');
    } else if (targetSourceType === TARGET_MENU_SERVICE) {
      targetMenuName = t('SelectTargetMenuModal.target_menu_service');
    }

    return targetMenuName;
  }

  function getConfirmAddMenuMessage() {
    let message = null;

    if (targetSourceType === TARGET_MENU_OVERLAY_FUNCTION) {
      message = t('SelectTargetMenuModal.warn_confirm_add_menu_overlay_function');
    } else if (targetSourceType === TARGET_MENU_ORDER_QUANTITY_LIMIT) {
      message = t('SelectTargetMenuModal.warn_confirm_add_menu_quantity_order_limit');
    } else if (targetSourceType === TARGET_MENU_DISPLAY_CONTROL_TIMING) {
      message = t('SelectTargetMenuModal.warn_confirm_add_menu_display_control_timing');
    } else if (targetSourceType === TARGET_MENU_SERVICE) {
      message = t('SelectTargetMenuModal.warn_confirm_add_menu_service');
    }

    return message;
  }

  function handleFilter(formSearchValue: FormSearchValueProps) {
    setFilter(formSearchValue);
  }

  function handleReload() {
    loadTargetMenuData();
  }

  function handleReflect(menuIds: number[]) {
    // confirm add menu
    const confirmMessage = getConfirmAddMenuMessage();
    if (confirmMessage) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={confirmMessage}
          onConfirm={() => {
            onReflect?.(menuIds);
            handleClose();
          }}
        />
      ));
      return;
    }

    onReflect?.(menuIds);
    handleClose();
  }

  // compose value
  const targetMenuName = getTargetMenuName();

  return (
    <Modal dialogClassName="select-target-menu-modal" show backdrop="static" centered onHide={handleClose}>
      <Modal.Header
        closeButton
        className={`bg-primary1 justify-content-center ${isEditMode ? 'reedit-mode-wrapper' : ''}`}
      >
        <FeatureTitleBar title={t('SelectTargetMenuModal.head_title')}></FeatureTitleBar>
      </Modal.Header>

      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        {targetMenuName && <div className="fw-bold mb-0">{targetMenuName}</div>}
        <SelectTargetMenuModalFilter onFilter={handleFilter} />
        <SelectTargetMenuModalTable data={targetMenus} onReload={handleReload} onReflect={handleReflect} />
      </Modal.Body>
    </Modal>
  );
}

export default SelectTargetMenuModal;
